import React, { useState, useEffect } from 'react'
import CoverLetterProvider, { useCoverLetter } from './CoverLetterProvider'
import { TemplateProvider, useTemplate } from '~/lib/pdf'
import { Textarea } from '~/lib/ui/textarea'
import { Button } from '~/lib/ui/button'
import { Toaster } from '~/lib/ui/toast'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
  setTabNavActiveTab,
} from '~/components/shared/TabNav'
import {
  DocumentMagnifyingGlassIcon,
  IdentificationIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import Editor from './Editor/index'
import PdfViewerPanel from './PdfViewerPanel'
import StyleEditor from './StyleEditor'
import { templates, templateStyle, templateSupportedStyles } from './templates'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import translations from './locales.json'

const CoverLetterBuilder = ({
  locale,
  template_settings,
  template_index,
  ...props
}) => {
  const defaultStyle = template_settings
    ? {
        ...templateStyle,
        [`template-${template_index}`]: template_settings,
      }
    : templateStyle
  const templateProps = {
    templates,
    defaultStyle,
    templateSupportedStyles,
    template_index,
    dev_mode: props.dev_mode,
  }
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope={'javascript.components.CoverLetterBuilder'}
    >
      <TemplateProvider {...templateProps}>
        <CoverLetterProvider locale={locale} {...props}>
          <CoverLetterBuilderInner />
        </CoverLetterProvider>
      </TemplateProvider>
    </TranslationProvider>
  )
}
export default CoverLetterBuilder

const CoverLetterBuilderInner = () => {
  const { devMode, documentId, previewTemplateIndex, setPreviewTemplateIndex } =
    useCoverLetter()
  const { templateIndex } = useTemplate()
  const defaultActiveTab = documentId ? 'details' : 'chooseTemplate'
  const onChangeActiveTab = (name) => {
    if (name == 'chooseTemplate') {
      setPreviewTemplateIndex(templateIndex)
    } else {
      setPreviewTemplateIndex(null)
    }
  }
  const { t } = useTranslation('Editor')

  return (
    <div className="flex w-full flex-col p-4 md:flex-row md:p-0">
      <div className="md:basis-3/5">
        <TabNav
          className="px-4 pt-4 lg:px-8 xl:px-12"
          defaultActiveTab={defaultActiveTab}
          onChangeActiveTab={onChangeActiveTab}
          mobileMode="swipe"
        >
          <ButtonContainer>
            <TabButton
              name="chooseTemplate"
              text={t('choose_template')}
              Icon={DocumentMagnifyingGlassIcon}
            />
            <TabButton
              name="details"
              text={t('details')}
              Icon={IdentificationIcon}
            />
            <TabButton
              name="formattingAndDesign"
              text={t('formatting_and_design')}
              Icon={ClipboardDocumentCheckIcon}
            />
            {devMode && (
              <TabButton name="text" text="Raw JSON" Icon={DocumentTextIcon} />
            )}
          </ButtonContainer>
          <PanelContainer className="md:min-h-[130vh]">
            <TabPanel name="chooseTemplate" className="bg-[#E0F2F9] pt-8">
              <SelectTemplatePanel />
            </TabPanel>
            <TabPanel name="details" className="pt-8">
              <Editor templateIndex={templateIndex} />
            </TabPanel>
            <TabPanel name="formattingAndDesign" className="pt-8">
              <StyleEditor />
            </TabPanel>
            <TabPanel name="text" className="pt-8">
              <TextEditor />
            </TabPanel>
          </PanelContainer>
        </TabNav>
      </div>
      <div id="resume-pdf-preview" className="relative md:flex-grow">
        <div className="h-screen w-full md:sticky md:left-0 md:top-0">
          {previewTemplateIndex === null ? (
            <PdfViewerPanel />
          ) : (
            <RightPanelTemplatePreview />
          )}
        </div>
      </div>
      <Toaster />
    </div>
  )
}

const SelectTemplatePanel = () => {
  const { previewTemplateIndex, setPreviewTemplateIndex } = useCoverLetter()
  const activeClasses = 'font-roboto-slab text-primary font-bold pb-2'
  const inactiveClasses = 'font-roboto-slab text-primary pb-2'
  const { t } = useTranslation('')

  const templateMap = {
    0: t('email'),
    1: t('standard'),
    2: t('networking'),
  }

  return (
    <div className="grid h-full sm:grid-cols-2 lg:grid-cols-3">
      {templates.map((_, index) => (
        <div className="flex flex-col items-center" key={`template-${index}`}>
          <span
            className={
              previewTemplateIndex == index ? activeClasses : inactiveClasses
            }
          >
            {templateMap[index]}
          </span>
          <button
            onClick={() => setPreviewTemplateIndex(index)}
            disabled={previewTemplateIndex === index}
            className={
              previewTemplateIndex === index
                ? 'rounded-xl border-4 border-solid border-primary'
                : 'm-1 rounded-xl'
            }
          >
            <TemplatePreview
              className="w-48 rounded-xl"
              templateIndex={index}
            />
          </button>
        </div>
      ))}
    </div>
  )
}

const RightPanelTemplatePreview = () => {
  const { t } = useTranslation()
  const { previewTemplateIndex } = useCoverLetter()
  const { setTemplateIndex } = useTemplate()
  const selectTemplateClick = () => {
    setTemplateIndex(previewTemplateIndex)
    setTabNavActiveTab('details')
  }

  return (
    <div className="flex flex-col bg-[#dadfe3] px-6 pt-8 md:h-full md:max-w-[35vw]">
      <div className="mb-4 flex flex-row items-center justify-around">
        <Button variant="default" onClick={selectTemplateClick}>
          {t('select_template')}
        </Button>
      </div>
      <TemplatePreview
        templateIndex={previewTemplateIndex}
        className="rounded-lg"
      />
    </div>
  )
}

const TemplatePreview = ({ className, templateIndex }) => {
  const { locale } = useCoverLetter()
  const localizedPath = locale == 'en' ? '' : `/${locale}`
  const url = `https://www-yns-com.s3.amazonaws.com/resume_builder${localizedPath}/CoverLetterTemplate${templateIndex}.png`

  return (
    <img className={className} src={url} alt={`Template ${templateIndex}`} />
  )
}

const TextEditor = () => {
  const { coverLetterDocument, setCoverLetterDocument } = useCoverLetter()
  const [editorCoverLetter, setEditorCoverLetter] = useState(
    JSON.stringify(coverLetterDocument, null, 2),
  )

  const sync = () => {
    const updatedCoverLetter = JSON.parse(editorCoverLetter)
    setCoverLetterDocument(updatedCoverLetter)
  }

  useEffect(() => {
    setEditorCoverLetter(JSON.stringify(coverLetterDocument, null, 2))
  }, [coverLetterDocument])

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-row justify-evenly">
        <Button onClick={sync}>Sync</Button>
      </div>
      <Textarea
        className="h-full w-full"
        value={editorCoverLetter}
        onChange={(e) => setEditorCoverLetter(e.target.value)}
      ></Textarea>
    </div>
  )
}
